import { useRef, useState, useEffect } from "react";
import SearchIcon from "@mui/icons-material/Search";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useTheme } from "@mui/material/styles";

// Typing placeholder hook
const useTypingPlaceholder = (phrases, typingSpeed = 80, deleteSpeed = 40, pauseDuration = 2000) => {
  const [placeholder, setPlaceholder] = useState("");
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const currentPhrase = phrases[phraseIndex];
    
    const timeout = setTimeout(() => {
      if (!isDeleting) {
        // Typing
        if (placeholder.length < currentPhrase.length) {
          setPlaceholder(currentPhrase.slice(0, placeholder.length + 1));
        } else {
          // Pause before deleting
          setTimeout(() => setIsDeleting(true), pauseDuration);
        }
      } else {
        // Deleting
        if (placeholder.length > 0) {
          setPlaceholder(placeholder.slice(0, -1));
        } else {
          setIsDeleting(false);
          setPhraseIndex((prev) => (prev + 1) % phrases.length);
        }
      }
    }, isDeleting ? deleteSpeed : typingSpeed);

    return () => clearTimeout(timeout);
  }, [placeholder, phraseIndex, isDeleting, phrases, typingSpeed, deleteSpeed, pauseDuration]);

  return placeholder;
};

function SearchBar({ query, setQuery, isSearching = false }) {
  const theme = useTheme();
  const [localQuery, setLocalQuery] = useState(query);
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const inputRef = useRef(null);

  const isDark = theme.palette.mode === "dark";
  
  // Typing placeholder animation
  const placeholderPhrases = [
    "search recipes...",
    "try 'pasta carbonara'",
    "find 'chicken curry'",
    "discover 'chocolate cake'",
    "explore 'vegan tacos'",
  ];
  const typingPlaceholder = useTypingPlaceholder(placeholderPhrases);

  // Accent colors
  const accentPrimary = isDark ? "#BB86FC" : "#6366f1";

  // Glassmorphism background
  const bgGradient = isDark
    ? "linear-gradient(135deg, rgba(30, 30, 35, 0.7) 0%, rgba(20, 20, 25, 0.8) 100%)"
    : "linear-gradient(135deg, rgba(255, 255, 255, 0.85) 0%, rgba(248, 250, 252, 0.9) 100%)";

  return (
    <form
      style={{
        fontFamily: "'JetBrains Mono', 'IBM Plex Mono', monospace",
        display: "flex",
        alignItems: "center",
        position: "relative",
        background: bgGradient,
        // Glassmorphism effect
        backdropFilter: "blur(16px)",
        WebkitBackdropFilter: "blur(16px)",
        borderRadius: "16px",
        border: `1px solid ${
          isFocused
            ? accentPrimary
            : isHovered
            ? isDark
              ? "rgba(255,255,255,0.25)"
              : "rgba(0,0,0,0.2)"
            : isDark
            ? "rgba(255,255,255,0.1)"
            : "rgba(0,0,0,0.08)"
        }`,
        boxShadow: isFocused
          ? `0 4px 20px -4px rgba(0,0,0,${isDark ? "0.4" : "0.12"})`
          : isHovered
          ? `0 4px 20px -4px rgba(0,0,0,${isDark ? "0.4" : "0.1"})`
          : `0 2px 8px -2px rgba(0,0,0,${isDark ? "0.3" : "0.06"})`,
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        overflow: "hidden",
      }}
      onSubmit={(e) => {
        e.preventDefault();
        setQuery(localQuery);
        inputRef.current.blur();
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >

      {/* Search icon on left */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          paddingLeft: "1.1em",
        }}
      >
        <SearchIcon
          style={{
            color: isFocused
              ? accentPrimary
              : isDark
              ? "rgba(255,255,255,0.4)"
              : "rgba(0,0,0,0.35)",
            fontSize: "1.25rem",
            transition: "color 0.3s ease",
          }}
        />
      </div>

      <input
        type="text"
        value={localQuery}
        onChange={(e) => setLocalQuery(e.target.value)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        ref={inputRef}
        placeholder={typingPlaceholder + (isFocused ? "|" : "")}
        style={{
          flex: 1,
          padding: "1em 0.85em",
          border: "none",
          outline: "none",
          color: theme.palette.text.primary,
          backgroundColor: "transparent",
          fontFamily: "'JetBrains Mono', 'IBM Plex Mono', monospace",
          fontSize: "0.95rem",
          fontWeight: 400,
          letterSpacing: "-0.01em",
          caretColor: accentPrimary,
        }}
        id="searchInput"
      />

      {/* Submit button with arrow - tinted style matching pagination */}
      <button
        type="submit"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: isFocused
            ? isDark
              ? "rgba(187, 134, 252, 0.2)"
              : "rgba(99, 102, 241, 0.15)"
            : isDark
            ? "rgba(255,255,255,0.08)"
            : "rgba(0,0,0,0.04)",
          border: isFocused
            ? isDark
              ? "1px solid rgba(187, 134, 252, 0.4)"
              : "1px solid rgba(99, 102, 241, 0.3)"
            : "1px solid transparent",
          cursor: "pointer",
          borderRadius: "10px",
          padding: "0.5em",
          margin: "0.5em 0.6em 0.5em 0",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
        onMouseEnter={(e) => {
          if (!isFocused) {
            e.currentTarget.style.background = isDark
              ? "rgba(255,255,255,0.12)"
              : "rgba(0,0,0,0.08)";
          }
        }}
        onMouseLeave={(e) => {
          if (!isFocused) {
            e.currentTarget.style.background = isDark
              ? "rgba(255,255,255,0.08)"
              : "rgba(0,0,0,0.04)";
          }
        }}
      >
        <ArrowForwardIcon
          style={{
            color: isFocused
              ? accentPrimary
              : isDark
              ? "rgba(255,255,255,0.6)"
              : "rgba(0,0,0,0.5)",
            fontSize: "1.1rem",
            transition: "color 0.3s ease",
          }}
        />
      </button>

      {/* Subtle animated background shimmer on focus */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(90deg, transparent, ${
            isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.01)"
          }, transparent)`,
          opacity: isFocused ? 1 : 0,
          pointerEvents: "none",
          transition: "opacity 0.3s ease",
        }}
      />
    </form>
  );
}

export default SearchBar;
